import type { BalanceResponse, TransactionEvent } from "@developerz.ai/ggcoin-sdk";
import { A } from "@solidjs/router";
import { createEffect, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { Money } from "../components/Money.tsx";
import { TransactionFeedRow } from "../components/TransactionFeedRow.tsx";

/**
 * Landing view once signed in. Shows the primary wallet's balance and a live
 * feed of recent transaction events from the SSE channel. The master wallet id
 * comes from the shared cache; a manual-paste form is the offline safety net.
 */
export function Dashboard() {
  const { client, store, masterWallet, ensureMaster, setMasterStatus } = useAuth();
  const [primaryWalletId, setPrimaryWalletId] = createSignal<string | null>(
    store.readWallets().primaryWalletId,
  );
  const [walletInput, setWalletInput] = createSignal(primaryWalletId() ?? "");
  const [balance, setBalance] = createSignal<BalanceResponse | null>(null);
  // Freeze status comes from the shared master-wallet cache (no per-mount fetch).
  const status = () => masterWallet()?.status ?? null;
  const [freezing, setFreezing] = createSignal(false);
  const [copied, setCopied] = createSignal(false);
  const [feed, setFeed] = createSignal<TransactionEvent[]>([]);
  const [error, setError] = createSignal<unknown | null>(null);
  const [loading, setLoading] = createSignal(false);

  // Monotonic request id — an SSE burst can fire several refreshes whose
  // responses land out of order; only the most-recent applies.
  let balanceReq = 0;
  async function refreshBalance(opts: { showLoading?: boolean } = {}) {
    const id = primaryWalletId();
    if (!id) return;
    const reqId = ++balanceReq;
    if (opts.showLoading) setLoading(true);
    try {
      const res = await client.getBalance(id);
      if (reqId === balanceReq) {
        setBalance(res);
        setError(null);
      }
    } catch (err) {
      if (reqId === balanceReq) setError(err);
    } finally {
      if (opts.showLoading) setLoading(false);
    }
  }

  onMount(() => {
    // Resolve the master wallet via the shared cache (deduped); populates the
    // freeze status too. Never overrides a manually-pasted id.
    ensureMaster()
      .then((res) => {
        setPrimaryWalletId((prev) => prev ?? res.id);
        setWalletInput((prev) => prev || res.id);
      })
      .catch((err) => setError(err));

    // Per-account SSE feed — refresh the balance live on each event.
    const unsubscribe = client.streamTransactions({
      onEvent: (event) => {
        setFeed((prev) => [event, ...prev].slice(0, 25));
        void refreshBalance();
      },
      // A transient SSE drop (e.g. the tab was backgrounded) fires an error but
      // the browser auto-reconnects — don't surface it as a hard error banner
      // (matches History/Approvals). Real fetch errors still show via the
      // balance refresh below.
      onError: () => {},
    });
    onCleanup(() => {
      void Promise.resolve(unsubscribe()).catch(() => {});
    });
  });

  // Refresh the balance whenever the resolved wallet id changes.
  createEffect(() => {
    if (primaryWalletId()) void refreshBalance({ showLoading: true });
  });

  async function toggleFreeze() {
    const id = primaryWalletId();
    const cur = status();
    if (!id || !cur) return;
    setFreezing(true);
    setError(null);
    try {
      const next = cur === "active" ? "frozen" : "active";
      const res = await client.setWalletStatus(id, { status: next });
      setMasterStatus(res.status);
    } catch (err) {
      setError(err);
    } finally {
      setFreezing(false);
    }
  }

  function onSaveWallet(e: Event) {
    e.preventDefault();
    const next = walletInput().trim();
    if (!next) return;
    store.setPrimaryWallet(next);
    setBalance(null);
    setError(null);
    setPrimaryWalletId(next);
  }

  return (
    <>
      <ErrorBanner error={error()} />

      <section class="card">
        <h2>Primary wallet</h2>
        <Show
          when={primaryWalletId()}
          fallback={
            <form onSubmit={onSaveWallet}>
              <p class="muted" style={{ "margin-top": 0 }}>
                No wallet linked on this device yet. Paste your master wallet id to get started.
              </p>
              <label for="wallet-input">Master wallet id</label>
              <input
                id="wallet-input"
                value={walletInput()}
                onInput={(e) => setWalletInput(e.currentTarget.value)}
                placeholder="00000000-0000-7000-8000-000000000000"
              />
              <button
                type="submit"
                class="primary"
                style={{ "margin-top": "0.5rem" }}
                disabled={!walletInput().trim()}
              >
                Save
              </button>
            </form>
          }
        >
          {(wid) => (
            <>
              <div class="row">
                <div>
                  <div class="muted" style={{ "font-size": "0.875rem" }}>
                    Wallet id <span class="muted">— share this to receive transfers</span>
                  </div>
                  <div class="status-line">
                    <span class="feed-id">{wid()}</span>
                    <button
                      type="button"
                      class="secondary small"
                      onClick={() => {
                        void navigator.clipboard?.writeText(wid());
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 1500);
                      }}
                    >
                      {copied() ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                </div>
                <div>
                  <div class="muted" style={{ "font-size": "0.875rem" }}>
                    Balance
                  </div>
                  <div style={{ "font-size": "1.6rem" }}>
                    <Show when={!loading()} fallback={"…"}>
                      <Money amount={balance()?.balance ?? "0"} />
                    </Show>
                  </div>
                </div>
              </div>
              <Show when={status()}>
                {(st) => (
                  <>
                    <div class="status-line">
                      <span class={`badge badge-${st()}`}>{st()}</span>
                      <button
                        type="button"
                        class={`small ${st() === "active" ? "secondary danger" : "primary"}`}
                        disabled={freezing()}
                        onClick={toggleFreeze}
                      >
                        {freezing() ? "…" : st() === "active" ? "Freeze" : "Unfreeze"}
                      </button>
                    </div>
                    <Show when={st() === "frozen"}>
                      <p class="status-hint">Frozen — spends are blocked until you unfreeze.</p>
                    </Show>
                  </>
                )}
              </Show>
              <div class="row" style={{ "margin-top": "0.75rem" }}>
                <A href="/send" class="link-button">
                  Send →
                </A>
                <A href="/sub-wallets" class="link-button">
                  Manage sub-wallets →
                </A>
              </div>
            </>
          )}
        </Show>
      </section>

      <section class="card">
        <h2>Live activity</h2>
        <Show
          when={feed().length > 0}
          fallback={
            <p class="muted" style={{ "margin-top": 0 }}>
              Waiting for the next transaction…
            </p>
          }
        >
          <ul class="feed">
            <For each={feed()}>{(event) => <TransactionFeedRow event={event} />}</For>
          </ul>
        </Show>
      </section>
    </>
  );
}
